/**
 * client/ui/potions.ts
 * Responsabilidad: Controlador de UI para La Mesa del Alquimista (Tienda de Pociones)
 * y la barra HUD in-game con ranuras Q, W, E y cooldown global.
 */

import Swal from "sweetalert2";
import {
    potionManager,
    POTION_CATALOG,
    POTION_MAX_BELT_SLOTS,
    type PotionId,
    type DailyShopAdType,
    type DailyShopAdsStatus,
} from "../potions";
import { safeGetStorage } from "../utils";
import { showRewardedAd } from "../poki";
import { emoteManager } from "../emotes";

export interface PotionsUIOptions {
    onUsePotion?: (slotIndex: number) => void;
    onOpen?: () => void;
    onClose?: () => void;
}

export class PotionsUI {
    private options: PotionsUIOptions;

    // Elementos del Modal
    private modal = document.getElementById("potions-modal");
    private closeBtn = document.getElementById("potions-modal-close");
    private beltCountBadge = document.getElementById("potions-belt-count");
    private beltPreviewContainer = document.getElementById("potions-belt-slots-preview");
    private crystalsDisplay = document.getElementById("potions-modal-crystals");
    private catalogGrid = document.getElementById("potions-catalog-grid");

    // Sprint 31: Botones de Ofertas Diarias por Video
    private btnDailyAdPotion = document.getElementById("btn-daily-ad-potion") as HTMLButtonElement | null;
    private btnDailyAdCrystals = document.getElementById("btn-daily-ad-crystals") as HTMLButtonElement | null;
    private btnDailyAdOrbs = document.getElementById("btn-daily-ad-orbs") as HTMLButtonElement | null;

    // Elementos de la Barra HUD
    private hudBar = document.getElementById("potion-bar");
    private hudSlots: HTMLElement[] = [];
    private elementLockBanner = document.getElementById("element-lock-hud-banner");
    private elementLockTimer = document.getElementById("element-lock-hud-timer");

    private lastCooldownSeconds: number = -1;

    constructor(options: PotionsUIOptions = {}) {
        this.options = options;
        this.init();
    }

    private init(): void {
        // Inicializar listeners del Modal
        if (this.closeBtn) {
            this.closeBtn.addEventListener("click", () => this.close());
        }

        if (this.modal) {
            this.modal.addEventListener("click", (e) => {
                if (e.target === this.modal) this.close();
            });
        }

        // Inicializar listeners de las ofertas diarias de video
        if (this.btnDailyAdPotion) {
            this.btnDailyAdPotion.addEventListener("click", () => this.handleDailyAd("potion"));
        }
        if (this.btnDailyAdCrystals) {
            this.btnDailyAdCrystals.addEventListener("click", () => this.handleDailyAd("crystals"));
        }
        if (this.btnDailyAdOrbs) {
            this.btnDailyAdOrbs.addEventListener("click", () => this.handleDailyAd("orbs"));
        }

        // Inicializar slots de la barra HUD
        if (this.hudBar) {
            for (let i = 0; i < POTION_MAX_BELT_SLOTS; i++) {
                const slotEl = document.getElementById(`potion-hud-slot-${i}`);
                if (slotEl) {
                    this.hudSlots.push(slotEl);
                    slotEl.addEventListener("click", (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.options.onUsePotion?.(i);
                    });
                }
            }
        }

        // Suscribirse a cambios en potionManager
        potionManager.subscribe((belt, warehouse, crystals, _orbs, dailyAds) => {
            this.renderBelt(belt);
            this.renderWarehouse(warehouse, belt);
            this.renderCrystals(crystals);
            this.renderCatalog(crystals);
            this.renderDailyAds(dailyAds);
        });

        this.updateDailyAdsUI();
    }

    public updateDailyAdsUI(): void {
        this.renderDailyAds();
    }

    public open(): void {
        if (!this.modal) return;
        this.modal.classList.remove("hidden");
        void potionManager.refreshStatus();
        this.options.onOpen?.();
    }

    public close(): void {
        if (!this.modal) return;
        this.modal.classList.add("hidden");
        this.options.onClose?.();
    }

    public isOpen(): boolean {
        return !!this.modal && !this.modal.classList.contains("hidden");
    }

    public showHUD(): void {
        if (this.hudBar) {
            this.hudBar.classList.remove("hidden");
        }
    }

    public hideHUD(): void {
        if (this.hudBar) {
            this.hudBar.classList.add("hidden");
        }
        if (this.elementLockBanner) {
            this.elementLockBanner.classList.add("hidden");
        }
        const buffsBar = document.getElementById("potion-active-buffs-bar");
        if (buffsBar) {
            buffsBar.classList.add("hidden");
            buffsBar.innerHTML = "";
        }
    }

    /**
     * Actualiza el estado visual de los 3 slots de la barra HUD en combate
     * y la barra flotante de buffs activos (#potion-active-buffs-bar).
     */
    public updateHUD(player: {
        potionSlot0?: string;
        potionSlot1?: string;
        potionSlot2?: string;
        potionCooldownUntil?: number;
        drinkingUntil?: number;
        elementLockedUntil?: number;
        speedBoostUntil?: number;
        magnetUntil?: number;
        cosmicFoodUntil?: number;
        rebirthUntil?: number;
        activePotionEffect?: string;
    } | null): void {
        if (!player) {
            this.hideHUD();
            return;
        }

        this.showHUD();
        const now = Date.now();
        const slots = [player.potionSlot0 || "", player.potionSlot1 || "", player.potionSlot2 || ""];
        const cooldownRemaining = Math.max(0, (player.potionCooldownUntil || 0) - now);
        const isDrinking = (player.drinkingUntil || 0) > now;

        for (let i = 0; i < POTION_MAX_BELT_SLOTS; i++) {
            const slotEl = this.hudSlots[i];
            if (!slotEl) continue;

            const potionId = slots[i] as PotionId;
            const iconEl = slotEl.querySelector(".potion-hud-icon") as HTMLElement | null;
            const cooldownOverlay = slotEl.querySelector(".potion-hud-cooldown-overlay") as HTMLElement | null;

            const potion = potionId ? (potionManager.getCatalogList().find((p) => p.id === potionId) || POTION_CATALOG[potionId]) : null;
            if (potionId && potion) {
                slotEl.classList.remove("empty");
                if (iconEl) {
                    iconEl.textContent = potion.icon;
                    iconEl.title = `${potion.name}: ${potion.description}`;
                }

                // Indicador visual de estar bebiendo
                if (isDrinking) {
                    slotEl.classList.add("drinking");
                } else {
                    slotEl.classList.remove("drinking");
                }

                // Indicador visual de cooldown global (10s)
                if (cooldownOverlay) {
                    if (cooldownRemaining > 0) {
                        cooldownOverlay.classList.remove("hidden");
                        cooldownOverlay.textContent = `${(cooldownRemaining / 1000).toFixed(1)}s`;
                    } else {
                        cooldownOverlay.classList.add("hidden");
                    }
                }
            } else {
                slotEl.classList.add("empty");
                slotEl.classList.remove("drinking");
                if (iconEl) {
                    iconEl.textContent = "—";
                    iconEl.title = "Ranura vacía";
                }
                if (cooldownOverlay) {
                    cooldownOverlay.classList.add("hidden");
                }
            }
        }

        // Barra HUD de Buffs Activos de Pociones (#potion-active-buffs-bar)
        const buffsBar = document.getElementById("potion-active-buffs-bar");
        if (buffsBar) {
            const buffs: Array<{ id: string; name: string; icon: string; color: string; ms: number }> = [];
            if ((player.speedBoostUntil || 0) > now && player.activePotionEffect === "speed") {
                buffs.push({ id: "speed", name: "Velocidad", icon: "⚡", color: "#00e5ff", ms: (player.speedBoostUntil || 0) - now });
            }
            if ((player.magnetUntil || 0) > now) {
                buffs.push({ id: "magnet", name: "Imán", icon: "🧲", color: "#ff4081", ms: (player.magnetUntil || 0) - now });
            }
            if ((player.cosmicFoodUntil || 0) > now) {
                buffs.push({ id: "cosmic_food", name: "Comida x3", icon: "🌟", color: "#a855f7", ms: (player.cosmicFoodUntil || 0) - now });
            }
            if ((player.rebirthUntil || 0) > now) {
                buffs.push({ id: "rebirth", name: "Renacer 50%", icon: "🕊️", color: "#10b981", ms: (player.rebirthUntil || 0) - now });
            }
            if ((player.elementLockedUntil || 0) > now) {
                buffs.push({ id: "element_lock", name: "Bloqueado", icon: "🔒", color: "#ffd740", ms: (player.elementLockedUntil || 0) - now });
            }

            if (buffs.length > 0) {
                buffsBar.classList.remove("hidden");
                buffsBar.innerHTML = "";
                for (const buff of buffs) {
                    const isExpiring = buff.ms <= 3000;
                    const pill = document.createElement("div");
                    pill.className = `potion-buff-pill ${isExpiring ? "expiring" : ""}`;
                    pill.style.setProperty("--buff-color", buff.color);

                    const totalSecs = Math.max(0, Math.ceil(buff.ms / 1000));
                    const mins = Math.floor(totalSecs / 60);
                    const secs = totalSecs % 60;
                    const timeStr = `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;

                    pill.innerHTML = `
                        <span class="potion-buff-icon">${buff.icon}</span>
                        <span class="potion-buff-name">${buff.name}</span>
                        <span class="potion-buff-timer">${timeStr}</span>
                    `;
                    buffsBar.appendChild(pill);
                }
            } else {
                buffsBar.classList.add("hidden");
                buffsBar.innerHTML = "";
            }
        }

        // Actualizar Alerta de Bloqueo Elemental
        const lockRemaining = Math.max(0, (player.elementLockedUntil || 0) - now);
        const elBtns = document.querySelectorAll(".mobile-el-btn");

        if (lockRemaining > 0) {
            if (this.elementLockBanner) {
                this.elementLockBanner.classList.remove("hidden");
                if (this.elementLockTimer) {
                    this.elementLockTimer.textContent = `${(lockRemaining / 1000).toFixed(1)}s`;
                }
            }
            elBtns.forEach((btn) => btn.classList.add("element-btn-locked"));
        } else {
            if (this.elementLockBanner) {
                this.elementLockBanner.classList.add("hidden");
            }
            elBtns.forEach((btn) => btn.classList.remove("element-btn-locked"));
        }
    }

    private renderBelt(belt: (string | null)[]): void {
        const count = belt.filter(Boolean).length;
        if (this.beltCountBadge) {
            this.beltCountBadge.textContent = `${count}/${POTION_MAX_BELT_SLOTS}`;
            if (count >= POTION_MAX_BELT_SLOTS) {
                this.beltCountBadge.classList.add("full");
            } else {
                this.beltCountBadge.classList.remove("full");
            }
        }

        if (this.beltPreviewContainer) {
            this.beltPreviewContainer.innerHTML = "";
            const keys = ["Q", "W", "E"];
            for (let i = 0; i < POTION_MAX_BELT_SLOTS; i++) {
                const potionId = belt[i] as PotionId;
                const slot = document.createElement("div");
                slot.className = `belt-slot-preview ${potionId ? "filled" : "empty"}`;

                const potion = potionId ? (potionManager.getCatalogList().find((p) => p.id === potionId) || POTION_CATALOG[potionId]) : null;
                if (potionId && potion) {
                    slot.textContent = potion.icon;
                    slot.title = `${potion.name} [${keys[i]}] - Haz clic para desequipar y devolver al almacén`;
                    slot.style.cursor = "pointer";
                    slot.addEventListener("click", () => {
                        void this.handleUnequip(i);
                    });
                } else {
                    slot.textContent = "—";
                    slot.title = `Ranura [${keys[i]}] libre - Clic en una poción del almacén para equipar`;
                }

                const keyTag = document.createElement("span");
                keyTag.className = "belt-slot-key-tag";
                keyTag.textContent = keys[i];
                slot.appendChild(keyTag);

                this.beltPreviewContainer.appendChild(slot);
            }
        }
    }

    private renderWarehouse(warehouse: string[], belt: (string | null)[]): void {
        const container = document.getElementById("potions-warehouse-grid");
        if (!container) return;
        container.innerHTML = "";

        const catalog = potionManager.getCatalogList();
        const counts: Record<string, number> = {};
        for (const id of warehouse) {
            counts[id] = (counts[id] || 0) + 1;
        }

        const equippedCounts: Record<string, number> = {};
        for (const id of belt) {
            if (id) {
                equippedCounts[id] = (equippedCounts[id] || 0) + 1;
            }
        }

        const uniquePotionIds = Object.keys(counts);
        if (uniquePotionIds.length === 0) {
            const emptyEl = document.createElement("div");
            emptyEl.className = "warehouse-empty-notice";
            emptyEl.textContent = "Tu almacén de pociones está vacío. ¡Adquiere tus primeros elixires abajo en la Tienda!";
            container.appendChild(emptyEl);
            return;
        }

        for (const id of uniquePotionIds) {
            const total = counts[id] || 0;
            const equipped = equippedCounts[id] || 0;
            const available = Math.max(0, total - equipped);
            const potion = catalog.find((p) => p.id === id) || POTION_CATALOG[id as PotionId];
            if (!potion) continue;

            const card = document.createElement("div");
            card.className = `warehouse-potion-card ${available === 0 ? "all-equipped" : ""}`;
            card.style.setProperty("--potion-color", potion.color);

            const rarityLabel = potion.rarity === "epic" ? "Épico" : potion.rarity === "rare" ? "Raro" : "Común";

            card.innerHTML = `
                <div class="warehouse-potion-header">
                    <div class="warehouse-potion-icon">${potion.icon}</div>
                    <div class="warehouse-potion-info">
                        <span class="warehouse-potion-name">${potion.name}</span>
                        <span class="warehouse-potion-sub">⏳ ${(potion.durationMs / 1000).toFixed(0)}s · ${rarityLabel}</span>
                    </div>
                </div>
                <div class="warehouse-potion-footer">
                    <div class="warehouse-potion-count-pill">
                        x${total} ${available < total ? `(${available} disp.)` : "(todas disp.)"}
                    </div>
                    <button type="button" class="warehouse-equip-btn" ${available === 0 ? "disabled" : ""}>
                        ${available > 0 ? "Equipar ➔" : "✓ Equipada"}
                    </button>
                </div>
            `;

            if (available > 0) {
                const triggerEquip = (e?: Event) => {
                    if (e) e.stopPropagation();
                    const emptyIdx = belt.findIndex((s) => !s);
                    if (emptyIdx === -1) {
                        void Swal.fire({
                            title: "Cinturón Lleno (3/3)",
                            text: "Tus 3 ranuras tácticas [Q, W, E] están ocupadas. Haz clic en una ranura arriba para desequiparla primero.",
                            icon: "info",
                            confirmButtonText: "Entendido",
                            confirmButtonColor: "#38bdf8",
                            background: "#0f172a",
                            color: "#f8fafc",
                        });
                        return;
                    }
                    void this.handleEquip(emptyIdx, id);
                };

                const equipBtn = card.querySelector<HTMLButtonElement>(".warehouse-equip-btn");
                if (equipBtn) {
                    equipBtn.addEventListener("click", triggerEquip);
                }
                card.style.cursor = "pointer";
                card.addEventListener("click", triggerEquip);
            }

            container.appendChild(card);
        }
    }

    private async handleEquip(slotIndex: number, potionId: string): Promise<void> {
        const success = await potionManager.equipSlot(slotIndex, potionId);
        if (success) {
            const potion = potionManager.getCatalogList().find((p) => p.id === potionId) || POTION_CATALOG[potionId as PotionId];
            const keys = ["Q", "W", "E"];
            const Toast = Swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 1500,
                background: "#0f172a",
                color: "#f8fafc",
            });
            void Toast.fire({
                icon: "success",
                title: `${potion ? potion.icon : "🧪"} Equipada en Ranura [${keys[slotIndex]}]`,
            });
        }
    }

    private async handleUnequip(slotIndex: number): Promise<void> {
        const success = await potionManager.unequipSlot(slotIndex);
        if (success) {
            const keys = ["Q", "W", "E"];
            const Toast = Swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 1500,
                background: "#0f172a",
                color: "#f8fafc",
            });
            void Toast.fire({
                icon: "info",
                title: `Ranura [${keys[slotIndex]}] desequipada (guardada en almacén)`,
            });
        }
    }

    private renderCrystals(crystals: number): void {
        if (this.crystalsDisplay) {
            this.crystalsDisplay.textContent = `${crystals} 💎`;
        }
    }

    private selectedRarityFilter: "all" | "common" | "rare" | "epic" = "all";
    private selectedSortOrder: "default" | "price_asc" | "price_desc" = "default";
    private currentPage: number = 1;
    private readonly pageSize: number = 8;

    private renderFilterBar(): void {
        if (!this.catalogGrid || !this.catalogGrid.parentElement) return;

        let filterBar = document.getElementById("potions-filter-bar");
        if (!filterBar) {
            filterBar = document.createElement("div");
            filterBar.id = "potions-filter-bar";
            filterBar.className = "shop-filter-bar";
            this.catalogGrid.parentElement.insertBefore(filterBar, this.catalogGrid);
        }

        filterBar.innerHTML = `
            <div class="filter-rarity-group">
                <button type="button" class="filter-pill-btn ${this.selectedRarityFilter === "all" ? "active" : ""}" data-rarity="all">Todos</button>
                <button type="button" class="filter-pill-btn ${this.selectedRarityFilter === "common" ? "active" : ""}" data-rarity="common">Comunes</button>
                <button type="button" class="filter-pill-btn ${this.selectedRarityFilter === "rare" ? "active" : ""}" data-rarity="rare">Raros</button>
                <button type="button" class="filter-pill-btn ${this.selectedRarityFilter === "epic" ? "active" : ""}" data-rarity="epic">Épicos</button>
            </div>
            <select class="shop-sort-select">
                <option value="default" ${this.selectedSortOrder === "default" ? "selected" : ""}>Orden por Defecto</option>
                <option value="price_asc" ${this.selectedSortOrder === "price_asc" ? "selected" : ""}>Precio: Menor a Mayor ⬆</option>
                <option value="price_desc" ${this.selectedSortOrder === "price_desc" ? "selected" : ""}>Precio: Mayor a Menor ⬇</option>
            </select>
        `;

        filterBar.querySelectorAll<HTMLButtonElement>(".filter-pill-btn").forEach((btn) => {
            btn.addEventListener("click", () => {
                const rarity = btn.getAttribute("data-rarity") as "all" | "common" | "rare" | "epic";
                if (rarity && this.selectedRarityFilter !== rarity) {
                    this.selectedRarityFilter = rarity;
                    this.currentPage = 1;
                    this.renderCatalog(potionManager.getCrystals());
                }
            });
        });

        const sortSelect = filterBar.querySelector<HTMLSelectElement>("select.shop-sort-select");
        if (sortSelect) {
            sortSelect.addEventListener("change", () => {
                this.selectedSortOrder = sortSelect.value as "default" | "price_asc" | "price_desc";
                this.currentPage = 1;
                this.renderCatalog(potionManager.getCrystals());
            });
        }
    }

    private renderPaginationBar(totalPages: number): void {
        if (!this.catalogGrid || !this.catalogGrid.parentElement) return;

        let paginationBar = document.getElementById("potions-pagination-bar");
        if (!paginationBar) {
            paginationBar = document.createElement("div");
            paginationBar.id = "potions-pagination-bar";
            paginationBar.className = "shop-pagination-bar";
            if (this.catalogGrid.nextSibling) {
                this.catalogGrid.parentElement.insertBefore(paginationBar, this.catalogGrid.nextSibling);
            } else {
                this.catalogGrid.parentElement.appendChild(paginationBar);
            }
        }

        paginationBar.innerHTML = `
            <button type="button" class="pagination-nav-btn prev-page-btn" ${this.currentPage <= 1 ? "disabled" : ""}>◀ Anterior</button>
            <span class="pagination-page-indicator">Página ${this.currentPage} de ${totalPages}</span>
            <button type="button" class="pagination-nav-btn next-page-btn" ${this.currentPage >= totalPages ? "disabled" : ""}>Siguiente ▶</button>
        `;

        const prevBtn = paginationBar.querySelector<HTMLButtonElement>(".prev-page-btn");
        const nextBtn = paginationBar.querySelector<HTMLButtonElement>(".next-page-btn");

        if (prevBtn && this.currentPage > 1) {
            prevBtn.addEventListener("click", () => {
                this.currentPage--;
                this.renderCatalog(potionManager.getCrystals());
            });
        }
        if (nextBtn && this.currentPage < totalPages) {
            nextBtn.addEventListener("click", () => {
                this.currentPage++;
                this.renderCatalog(potionManager.getCrystals());
            });
        }
    }

    private renderCatalog(_crystals: number): void {
        if (!this.catalogGrid) return;
        this.renderFilterBar();
        this.catalogGrid.innerHTML = "";

        const catalog = potionManager.getCatalogList();

        let filteredItems = [...catalog];
        if (this.selectedRarityFilter !== "all") {
            filteredItems = filteredItems.filter((potion) => (potion.rarity || "common") === this.selectedRarityFilter);
        }

        if (this.selectedSortOrder === "price_asc") {
            filteredItems.sort((a, b) => {
                const costA = a.currency === "orbs" ? (a.priceOrbs || a.priceCrystals) * 1000 : a.priceCrystals;
                const costB = b.currency === "orbs" ? (b.priceOrbs || b.priceCrystals) * 1000 : b.priceCrystals;
                return costA - costB;
            });
        } else if (this.selectedSortOrder === "price_desc") {
            filteredItems.sort((a, b) => {
                const costA = a.currency === "orbs" ? (a.priceOrbs || a.priceCrystals) * 1000 : a.priceCrystals;
                const costB = b.currency === "orbs" ? (b.priceOrbs || b.priceCrystals) * 1000 : b.priceCrystals;
                return costB - costA;
            });
        }

        const totalPages = Math.max(1, Math.ceil(filteredItems.length / this.pageSize));
        if (this.currentPage > totalPages) {
            this.currentPage = totalPages;
        }
        const pagedItems = filteredItems.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize);

        if (pagedItems.length === 0) {
            const emptyNotice = document.createElement("div");
            emptyNotice.className = "shop-empty-notice";
            emptyNotice.style.gridColumn = "1 / -1";
            emptyNotice.style.textAlign = "center";
            emptyNotice.style.padding = "2rem";
            emptyNotice.style.color = "rgba(255, 255, 255, 0.5)";
            emptyNotice.textContent = "No hay pociones en esta categoría.";
            this.catalogGrid.appendChild(emptyNotice);
        } else {
            for (const potion of pagedItems) {
                const id = potion.id;
                const card = document.createElement("div");
                card.className = "potion-card";
                card.style.setProperty("--potion-color", potion.color);
                card.style.setProperty("--potion-glow", `${potion.color}40`);

                const rarityLabel = potion.rarity === "epic" ? "Épico" : potion.rarity === "rare" ? "Raro" : "Común";
                const rarityClass = `rarity-tag-${potion.rarity || "common"}`;
                const basePrice = potion.currency === "orbs" ? (potion.priceOrbs || potion.priceCrystals) : potion.priceCrystals;
                const currencyIcon = potion.currency === "orbs" ? "🔮" : "💎";

                card.innerHTML = `
                    <div class="potion-card-header">
                        <div class="potion-card-icon-wrap">${potion.icon}</div>
                        <div class="potion-card-title-group">
                            <h3 class="potion-card-name">${potion.name}</h3>
                            <div class="potion-card-meta">
                                <span class="potion-card-duration">⏳ Duración: ${(potion.durationMs / 1000).toFixed(0)}s</span>
                                <span class="potion-rarity-badge ${rarityClass}">${rarityLabel}</span>
                            </div>
                        </div>
                    </div>
                    <p class="potion-card-desc">${potion.description}</p>
                    <div class="potion-card-footer">
                        <div class="potion-card-price-group">
                            <div class="potion-card-price">
                                <span class="potion-price-val">${basePrice}</span>
                                <span>${currencyIcon}</span>
                            </div>
                            <div class="potion-qty-wrapper">
                                <label class="potion-qty-label" for="qty-${id}">Cant:</label>
                                <select id="qty-${id}" class="potion-qty-select" data-potion-id="${id}">
                                    <option value="1" selected>1</option>
                                    <option value="2">2</option>
                                    <option value="3">3</option>
                                    <option value="4">4</option>
                                    <option value="5">5</option>
                                </select>
                            </div>
                        </div>
                        <button type="button" class="potion-buy-btn" data-potion-id="${id}">
                            Comprar
                        </button>
                    </div>
                `;

                const select = card.querySelector<HTMLSelectElement>(`.potion-qty-select`);
                const priceVal = card.querySelector<HTMLSpanElement>(`.potion-price-val`);
                if (select && priceVal) {
                    select.addEventListener("change", () => {
                        const qty = parseInt(select.value, 10) || 1;
                        priceVal.textContent = String(basePrice * qty);
                    });
                }

                const btn = card.querySelector<HTMLButtonElement>(".potion-buy-btn");
                if (btn) {
                    btn.addEventListener("click", () => {
                        const qty = select ? parseInt(select.value, 10) || 1 : 1;
                        void this.handleBuy(id, qty);
                    });
                }

                this.catalogGrid.appendChild(card);
            }
        }

        this.renderPaginationBar(totalPages);
    }

    private async handleBuy(potionId: string, quantity: number = 1): Promise<void> {
        const token =
            safeGetStorage("alchem_auth_token") ||
            safeGetStorage("colyseus-auth-token");

        if (!token) {
            void Swal.fire({
                title: "Inicia Sesión Requerido",
                text: "Para adquirir y guardar pociones en tu almacén y cinturón táctico, inicia sesión o regístrate en Alchem.io.",
                icon: "info",
                confirmButtonText: "Entendido",
                confirmButtonColor: "#38bdf8",
                background: "#0f172a",
                color: "#f8fafc",
            });
            return;
        }

        const potion = potionManager.getCatalogList().find((p) => p.id === potionId) || POTION_CATALOG[potionId as PotionId];
        if (!potion) return;

        const res = await potionManager.buyPotion(potionId, quantity);
        if (res.success) {
            const totalSpent = (potion.currency === "orbs" ? (potion.priceOrbs || potion.priceCrystals) : potion.priceCrystals) * quantity;
            const currencyIcon = potion.currency === "orbs" ? "🔮" : "💎";
            void Swal.fire({
                title: `¡${quantity > 1 ? `${quantity}x ` : ""}${potion.name} adquirida!`,
                text: `${quantity > 1 ? `${quantity} pociones guardadas` : "Poción guardada"} en tu almacén (${totalSpent} ${currencyIcon} gastados).`,
                icon: "success",
                timer: 2200,
                showConfirmButton: false,
                background: "#0f172a",
                color: "#f8fafc",
            });
        } else {
            void Swal.fire({
                title: "No se pudo comprar",
                text: res.message || "Ocurrió un error al procesar la compra.",
                icon: "warning",
                confirmButtonText: "Aceptar",
                confirmButtonColor: "#f59e0b",
                background: "#0f172a",
                color: "#f8fafc",
            });
        }
    }

    /**
     * Renderiza el estado de las 3 ofertas diarias de video en la tienda.
     * En modo Próximamente todos los botones permanecen deshabilitados.
     */
    private renderDailyAds(_status?: DailyShopAdsStatus | null): void {
        const items = [
            { btnId: "btn-daily-ad-potion", cardId: "daily-ad-card-potion" },
            { btnId: "btn-daily-ad-crystals", cardId: "daily-ad-card-crystals" },
            { btnId: "btn-daily-ad-orbs", cardId: "daily-ad-card-orbs" },
        ];
        for (const item of items) {
            const btn = document.getElementById(item.btnId) as HTMLButtonElement | null;
            const card = document.getElementById(item.cardId);
            if (!btn) continue;
            btn.disabled = true;
            btn.textContent = "🎬 Próximamente";
            btn.classList.remove("claimed");
            card?.classList.remove("claimed");
        }
    }

    /**
     * Procesa la visualización del video rewarded y la acreditación autoritativa de la recompensa.
     */
    private async handleDailyAd(adType: DailyShopAdType): Promise<void> {
        // En modo Próximamente los anuncios no se ejecutan por seguridad
        const adsDisabled = true as boolean;
        if (adsDisabled) return;

        const targetBtn = adType === "potion"
            ? this.btnDailyAdPotion
            : adType === "crystals"
            ? this.btnDailyAdCrystals
            : this.btnDailyAdOrbs;

        if (targetBtn) {
            targetBtn.disabled = true;
            targetBtn.textContent = "⏳ Viendo video...";
        }

        try {
            const watched = await showRewardedAd();
            if (!watched) {
                this.renderDailyAds();
                return;
            }

            const result = await potionManager.claimDailyAdReward(adType);
            this.renderDailyAds(result.dailyShopAds);

            if (result.success) {
                void emoteManager.refreshStatus();
                if (adType === "potion") {
                    const potion = result.grantedPotionId ? (potionManager.getCatalogList().find((p) => p.id === result.grantedPotionId) || POTION_CATALOG[result.grantedPotionId as PotionId]) : null;
                    const name = potion ? `${potion.icon} ${potion.name}` : "Poción Alquímica";
                    void Swal.fire({
                        title: "🧪 ¡Poción Obtenida!",
                        text: `Has recibido 1 ${name} añadida a tu almacén e inventario táctico.`,
                        icon: "success",
                        confirmButtonColor: "#38bdf8",
                        background: "#0f172a",
                        color: "#f8fafc",
                    });
                } else if (adType === "crystals") {
                    void Swal.fire({
                        title: "💎 ¡+20 Cristales Acreditados!",
                        text: "Tus Cristales Alquímicos diarios por ver el video han sido añadidos a tu saldo.",
                        icon: "success",
                        confirmButtonColor: "#38bdf8",
                        background: "#0f172a",
                        color: "#f8fafc",
                    });
                } else if (adType === "orbs") {
                    void Swal.fire({
                        title: "🔮 ¡+5 Orbes Acreditados!",
                        text: "Tus Piedras Filosofales diarias por ver el video han sido añadidas a tu saldo.",
                        icon: "success",
                        confirmButtonColor: "#a855f7",
                        background: "#0f172a",
                        color: "#f8fafc",
                    });
                }
            } else {
                void Swal.fire({
                    title: "No se pudo reclamar",
                    text: result.message || "Ocurrió un error al reclamar la recompensa diaria.",
                    icon: "warning",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "#f59e0b",
                    background: "#0f172a",
                    color: "#f8fafc",
                });
            }
        } catch (e) {
            console.error("[PotionsUI] Error en handleDailyAd:", e);
            this.renderDailyAds();
        }
    }
}

